import React from "react";
import { useTranslation } from "react-i18next";
import { EditBillDetailSettingsModal } from "@features/EditBillDetailSettingsModal/EditBillDetailSettingsModal";
import { CalculatedAmount } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/CalculatedAmount/CalculatedAmount";
import { BillDetailValueForm } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/ProviderBill/components/BillDetails/components/BillDetail/components/BillDetailValueForm/BillDetailValueForm";
import { IconButton } from "@components/IconButton/IconButton";
import { PencilIcon } from "@components/icons";
import { Notification } from "@components/Notification/Notification";
import { Tooltip } from "@components/Tooltip/Tooltip";
import { useModalActions } from "@store/modal/modal";
import {
  useUpdateBillDetailExpectedPaymentAmountMutation,
  useUpdateBillDetailTariffMutation,
  useUpdateBillDetailValueFromMutation,
  useUpdateBillDetailValueToMutation,
} from "@services/bills/mutations";
import {
  type IBillData,
  type IBillDetail,
  type IOverviewJointBillService,
} from "@services/overview/types";
import { isNullOrUndefined } from "@utils/common";
import { BillDetailMeterRule } from "./components/BillDetailMeterRule/BillDetailMeterRule";

interface IBillDetailProps {
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  jointBillServiceAgreementId: IBillData["jointBillServiceAgreementId"];
  periodId: number;
  billDetail: IBillDetail;
  currency: IBillData["currencyId"];
  isBillDataExpectedPaymentEditable: IBillData["expectedPaymentIsEditable"];
}

export const BillDetail: React.FC<IBillDetailProps> = ({
  jointBillServiceId,
  jointBillServiceAgreementId,
  periodId,
  billDetail,
  currency,
  isBillDataExpectedPaymentEditable,
}) => {
  const { t } = useTranslation();
  const { create } = useModalActions();
  const {
    name,
    billDetailId,
    valueFrom,
    valueTo,
    quantity,
    tariff,
    valueFromIsEditable,
    valueToIsEditable,
    isMeterReading,
    tariffIsEditable,
    expectedPaymentAmountIsEditable,
    factoryNumber,
    totalAmount,
    totalSum,
    message,
  } = billDetail;

  const { mutateAsync: updateValueFrom, isPending: isValueFromLoading } =
    useUpdateBillDetailValueFromMutation({
      jointBillServiceId,
      periodId,
      billDetailId,
    });

  const { mutateAsync: updateValueTo, isPending: isValueToLoading } =
    useUpdateBillDetailValueToMutation({
      jointBillServiceId,
      periodId,
      billDetailId,
    });

  const { mutateAsync: updateTariff, isPending: isTariffLoading } =
    useUpdateBillDetailTariffMutation({
      jointBillServiceId,
      periodId,
      billDetailId,
    });

  const {
    mutateAsync: updateExpectedPaymentAmount,
    isPending: isExpectedPaymentAmountLoading,
  } = useUpdateBillDetailExpectedPaymentAmountMutation({
    jointBillServiceId,
    periodId,
    billDetailId,
  });

  const editMeter = () => {
    create((modalProps) => (
      <EditBillDetailSettingsModal
        {...modalProps}
        jointBillServiceId={jointBillServiceId}
        jointBillServiceAgreementId={jointBillServiceAgreementId}
        billDetail={billDetail}
      />
    ));
  };

  return (
    <div className="flex flex-col gap-4 py-4 first:pt-0 last:pb-0">
      <div className="flex flex-col gap-4 md:flex-row md:justify-between">
        <div className="flex flex-col justify-between gap-1 md:justify-start">
          <div className="flex gap-2">
            <span>{name}</span>
            {isMeterReading && (
              <Tooltip content={t("bill.interactions.editMeter.tooltip")}>
                {(props) => (
                  <IconButton
                    {...props}
                    className="hidden md:block"
                    onClick={editMeter}
                    icon={PencilIcon}
                    iconClassName="h-4 w-4"
                    aria-label={t("bill.interactions.editMeter.tooltip")}
                  />
                )}
              </Tooltip>
            )}
          </div>
          <span className="text-grey-dark">{factoryNumber}</span>
        </div>
        <div className="flex flex-col flex-wrap gap-4 md:flex-row md:justify-end md:self-end">
          {isMeterReading && (
            <div className="flex flex-row gap-4">
              <div className="w-full md:w-32">
                <BillDetailValueForm
                  billDetailId={billDetailId}
                  name="valueFrom"
                  value={valueFrom}
                  label={t("billDetail.fields.valueFrom.label")}
                  isEditable={valueFromIsEditable}
                  onSubmit={updateValueFrom}
                  isLoading={isValueFromLoading}
                />
              </div>
              <div className="w-full md:w-32">
                <BillDetailValueForm
                  billDetailId={billDetailId}
                  name="valueTo"
                  value={valueTo}
                  label={t("billDetail.fields.valueTo.label")}
                  isEditable={valueToIsEditable}
                  onSubmit={updateValueTo}
                  isLoading={isValueToLoading}
                />
              </div>
            </div>
          )}
          {(isMeterReading ||
            (!isMeterReading &&
              !isBillDataExpectedPaymentEditable &&
              !isNullOrUndefined(totalAmount))) && (
            <div className="flex flex-row gap-4">
              {isMeterReading && (
                <div className="w-full md:w-32">
                  <BillDetailValueForm
                    value={quantity}
                    label={t("billDetail.fields.difference.label")}
                    isEditable={false}
                  />
                </div>
              )}
              {isMeterReading && !isNullOrUndefined(tariff) && (
                <div className="w-full md:w-32">
                  <BillDetailValueForm
                    billDetailId={billDetailId}
                    name="valueTariff"
                    value={tariff}
                    label={t("billDetail.fields.tariff.label")}
                    isEditable={tariffIsEditable}
                    onSubmit={updateTariff}
                    isLoading={isTariffLoading}
                  />
                </div>
              )}
              {!isMeterReading &&
                !isBillDataExpectedPaymentEditable &&
                !isNullOrUndefined(totalAmount) && (
                  <div className="flex flex-col justify-end">
                    <CalculatedAmount
                      totalSum={totalSum}
                      totalAmount={totalAmount}
                      currency={currency}
                    />
                  </div>
                )}
            </div>
          )}
          {!isNullOrUndefined(totalAmount) && (
            <div className="flex flex-row gap-4">
              <div className="w-full md:w-32">
                <BillDetailValueForm
                  billDetailId={billDetailId}
                  name="valueTotal"
                  value={totalAmount}
                  label={t("billDetail.fields.total.label")}
                  isEditable={expectedPaymentAmountIsEditable}
                  onSubmit={updateExpectedPaymentAmount}
                  isLoading={isExpectedPaymentAmountLoading}
                  isFixedNumber={true}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {isMeterReading && (
        <BillDetailMeterRule
          jointBillServiceId={jointBillServiceId}
          billDetailId={billDetailId}
          quantity={quantity}
        />
      )}
      {message && <Notification description={message} />}
    </div>
  );
};
